import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, RefreshCw, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdvisorNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestNumber = useRef(0);
  const inboxOpen = useRef(false);
  const enabled = ['advisor', 'admin'].includes(user?.role);

  const load = useCallback(async (offset = 0) => {
    if (!enabled) return;
    const request = ++requestNumber.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/advisor/notifications`, { params: { offset, limit: 20 }, withCredentials: true });
      if (request !== requestNumber.current) return;
      setItems((current) => offset ? [...current, ...data.items.filter((item) => !current.some((entry) => entry.id === item.id))] : data.items);
      setUnread(data.unread_count);
      setTotal(data.total);
      setError('');
    } catch (e) {
      if (request === requestNumber.current) setError('Could not load notifications. Please try again.');
    } finally {
      if (request === requestNumber.current) setLoading(false);
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    setItems([]); setUnread(0); setTotal(0);
    load();
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // Only refresh a closed inbox; don't disturb a reader's loaded pages.
    const timer = window.setInterval(() => { if (!inboxOpen.current && document.visibilityState === 'visible') load(); }, 30000);
    return () => {
      requestNumber.current += 1;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const handleOpenChange = (value) => {
    inboxOpen.current = value;
    setOpen(value);
    if (value) load();
  };

  const markRead = async (item) => {
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/notifications/${item.id}/read`, {}, { withCredentials: true });
      handleOpenChange(false);
      if (!item.read_at) setUnread((count) => Math.max(0, count - 1));
      navigate(`/advisor/submission/${item.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/notifications/read-all`, {}, { withCredentials: true });
      await load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!enabled) return null;
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative gap-2 text-white hover:bg-white/10 hover:text-white"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} data-testid="notifications-button">
          <Bell className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">Notifications</span>
          {unread > 0 && <span className="min-w-5 rounded-full bg-[#5b93d6] px-1.5 py-0.5 text-xs font-semibold text-white" data-testid="notifications-unread-count">{unread > 99 ? '99+' : unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={16} className="z-[60] w-[min(24rem,calc(100vw-2rem))] p-0 bg-white border-[#e2e8f0] text-[#0f172a] shadow-xl" data-testid="notifications-panel">
        <div className="border-b border-[#e2e8f0] p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold" data-testid="notifications-title">Completed Client Profiles</h2>
            <Button variant="ghost" size="icon" disabled={loading} onClick={() => load()} aria-label="Refresh notifications" data-testid="notifications-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[#475569]" aria-live="polite" data-testid="notifications-summary">{unread} unread</p>
            <Button variant="ghost" size="sm" disabled={!unread || busy} onClick={markAllRead} className="text-xs text-[#26518c]" data-testid="notifications-read-all">
              <CheckCheck className="h-4 w-4 mr-1" aria-hidden="true" />Mark all read
            </Button>
          </div>
        </div>
        {error && <p role="alert" className="p-4 text-sm text-red-600" data-testid="notifications-error">{error}</p>}
        <div className="max-h-[min(26rem,60vh)] overflow-y-auto" data-testid="notifications-list">
          {loading && !items.length && <p className="p-5 text-sm text-[#475569]" role="status" data-testid="notifications-loading">Loading notifications…</p>}
          {!loading && !error && !items.length && <p className="p-5 text-sm text-[#475569]" data-testid="notifications-empty">You're all caught up. New client submissions will appear here.</p>}
          {items.map((item) => (
            <button key={item.id} onClick={() => markRead(item)} disabled={busy}
              className={`w-full flex items-start gap-3 border-b border-[#e2e8f0] p-4 text-left transition-colors hover:bg-[#f8fafc] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#5b93d6] disabled:opacity-60 ${item.read_at ? '' : 'bg-[#5b93d6]/10'}`}
              data-testid={`notification-${item.id}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.read_at ? 'bg-transparent' : 'bg-[#5b93d6]'}`} aria-label={item.read_at ? 'Read' : 'Unread'} data-testid={`notification-read-state-${item.id}`} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold break-words" data-testid={`notification-name-${item.id}`}>{item.client_name}</span>
                <span className="block text-sm text-[#475569]">Submitted their Client Profile</span>
                <time dateTime={item.submitted_at} className="mt-1 block text-xs text-[#475569]" data-testid={`notification-time-${item.id}`}>{new Date(item.submitted_at).toLocaleString()}</time>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#26518c]">Review profile <ArrowUpRight className="h-3 w-3" aria-hidden="true" /></span>
              </span>
            </button>
          ))}
          {items.length < total && <div className="p-3"><Button variant="outline" className="w-full" disabled={loading} onClick={() => load(items.length)} data-testid="notifications-load-more">{loading ? 'Loading…' : 'Load more'}</Button></div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
